import { useCallback, useState } from 'react';
import useLocalStorage from './useLocalStorage';
import { ColumnSetting } from 'components/OrderGridColumnSettingsDialog';

const ORDER_GRID_SETTINGS_KEY = 'orderGridColumnSettings';

// Default column settings based on the current OrderHistoryGrid columns
const defaultColumnSettings: ColumnSetting[] = [
  { field: 'actions', headerName: 'Actions', visible: true, order: 0 },
  { field: 'orderDate', headerName: 'Order Date', visible: true, order: 1 },
  { field: 'dateLastUpdated', headerName: 'Last Updated', visible: true, order: 2 },
  { field: 'portfolioManager', headerName: 'PM', visible: true, order: 3 },
  { field: 'strategies', headerName: 'Strats', visible: true, order: 4 },
  { field: 'allocations', headerName: 'Splits', visible: true, order: 5 },
  { field: 'countries', headerName: 'Countries', visible: true, order: 6 },
  { field: 'orderDescriptionMessage', headerName: 'Order Details', visible: true, order: 7 },
  { field: 'price', headerName: 'Limit', visible: true, order: 8 },
  { field: 'instrument', headerName: 'Instrument', visible: true, order: 9 },
  { field: 'orderStatus', headerName: 'Status', visible: true, order: 10 },
  { field: 'orderInstruction', headerName: 'Instruction', visible: true, order: 11 },
  { field: 'orderReason', headerName: 'Reason', visible: true, order: 12 },
  { field: 'orderSeqId', headerName: 'Order ID', visible: true, order: 13 }
];

export default function useOrderGridSettings() {
  const [columnSettings, setColumnSettings] = useLocalStorage<ColumnSetting[]>(
    ORDER_GRID_SETTINGS_KEY,
    defaultColumnSettings
  );
  const [version, setVersion] = useState(0);

  const saveColumnSettings = useCallback((settings: ColumnSetting[]) => {
    setColumnSettings(settings);
    setVersion(prev => prev + 1);
  }, [setColumnSettings]);

  const clearColumnSettings = useCallback(() => {
    setColumnSettings(defaultColumnSettings);
    setVersion(prev => prev + 1);
  }, [setColumnSettings]);

  const forceUpdate = useCallback(() => {
    setVersion(prev => prev + 1);
  }, []);

  const getVisibleColumns = useCallback(() => {
    return columnSettings
      .filter((col: ColumnSetting) => col.visible)
      .sort((a: ColumnSetting, b: ColumnSetting) => a.order - b.order);
  }, [columnSettings]);

  const getColumnVisibilityModel = useCallback(() => {
    const visibilityModel: Record<string, boolean> = {};
    columnSettings.forEach((col: ColumnSetting) => {
      visibilityModel[col.field] = col.visible;
    });
    return visibilityModel;
  }, [columnSettings]);

  const getColumnOrder = useCallback(() => {
    return columnSettings
      .filter((col: ColumnSetting) => col.visible)
      .sort((a: ColumnSetting, b: ColumnSetting) => a.order - b.order)
      .map((col: ColumnSetting) => col.field);
  }, [columnSettings]);

  return {
    columnSettings,
    saveColumnSettings,
    clearColumnSettings,
    forceUpdate,
    getVisibleColumns,
    getColumnVisibilityModel,
    getColumnOrder,
    version
  };
} 